import Fuse, { type IFuseOptions } from 'fuse.js';
import { translateBlock } from 'i18n';
import _ from 'lodash';
import { UNCLASSIFIED } from 'shared/constants';

import { customizer } from '../helpers';

const GLOBAL = 'global';
// these types describe the output tree structure where fields can exists at other levels
export type DomainWithFields = Record<
    string,
    CategoryWithTableOnly | TableWithFields | TreeField[]
>;
export type TreeData = DomainWithFields;

// these types are for the standard tree structure
export type TableWithFields = Record<string, TreeField[]>;
export type CategoryWithTableOnly = Record<string, TableWithFields>;
type DomainWithCategoryOnly = Record<string, CategoryWithTableOnly>;
export type TreeDataWithoutIntermediateFields = DomainWithCategoryOnly;

export type TreeField = {
    aggregations?: Aggregation[];
    field: string;
    functions?: string[];
    label: string;
    rights?: string[];
    table: string;
    type: DataTypesT;
};

export type TreeOption = Omit<TreeField, 'label'>;

type FunctionCategory = Record<string, Record<string, string[]>>;

type Expanded = string[];

/**
 * Moves default_time from the 4th level to the 2nd one, and remove unclassified domain if empty
 * afterwards.
 * @param treeData the initial treeData
 * @returns treeData with the default_time hoisted to the 2nd level
 */
const moveDefaultTimeToGlobalDomain = (
    treeData: TreeDataWithoutIntermediateFields,
): TreeData => {
    // duplicate the treeData to avoid mutating it and hoist the default_time
    const newTreeData = { ...treeData };
    const otherTable = newTreeData[UNCLASSIFIED]?.[UNCLASSIFIED]?.other;
    if (!otherTable) return treeData;
    const defaultTimeIndex = otherTable?.findIndex(
        (field) => field.field === 'default_time',
    );
    if (defaultTimeIndex === -1) return treeData;
    // pop the defaultTimeIndex from the unclassified domain
    const defaultTime = newTreeData[UNCLASSIFIED][UNCLASSIFIED].other.splice(
        defaultTimeIndex,
        1,
    )[0];
    // remove the "other" table if now empty
    if (otherTable.length === 0)
        delete newTreeData[UNCLASSIFIED][UNCLASSIFIED].other;
    // remove the unclassified domain if now empty
    if (Object.keys(newTreeData[UNCLASSIFIED][UNCLASSIFIED]).length === 0) {
        delete newTreeData[UNCLASSIFIED];
    }
    return {
        ...newTreeData,
        global: {
            ...newTreeData.global,
            temporality: [defaultTime],
        },
    };
};
// TODO: a similar function will soon be used in the Cellar. Consider moving it to a common place.
export const getOptionFunction = (functions: string[]): string => {
    switch (functions.length) {
        case 0:
            return UNCLASSIFIED;
        case 1:
            return functions[0];
        default:
            return GLOBAL;
    }
};

// TODO: some recursive function to avoid the duplication of code and mutability would be nice
/** Transforms a list a options (metrics, filters, dimensions) into a tree with multiple levels.
 *
 * @params category - the list of categories for each table
 *
 * The returned tree structure is:
 *     function (hr, finance)
 *         -> category (ex absences, trainings)
 *             -> table (ex absence_daily)
 *                 -> field (ex absence_daily.day)
 *
 */
export const getDataInTreeFormat = (
    options: ReadonlyArray<TreeOption>,
    functionsCategories: FunctionCategory,
): TreeData => {
    const baseTreeData = options.reduce(
        (output: TreeDataWithoutIntermediateFields, option) => {
            const func = getOptionFunction(option.functions || []);
            if (!(func in output)) {
                output[func] = {};
            }

            const categories = functionsCategories[func] || {};
            const category =
                Object.keys(categories).find((item) =>
                    categories[item].find((table) => table === option.table),
                ) || UNCLASSIFIED;
            const table = option.table || UNCLASSIFIED;
            const field = {
                ...option,
                label: translateBlock(option.field),
            };

            _.mergeWith(
                output,
                {
                    [func]: {
                        [category]: { [table]: [field] },
                    },
                },
                customizer,
            );

            output[func][category][table] = _.sortBy(
                output[func][category][table],
                'label',
            );
            return output;
        },
        {},
    );
    const treeDataWithTemporalityDomain =
        moveDefaultTimeToGlobalDomain(baseTreeData);
    return treeDataWithTemporalityDomain;
};

// The type of the elements we pass to fuseJS to fuzzy search.
type FlatTreeField = {
    path: string;
    value: TreeField;
    table: string;
};

// The type of elements returned by fuseJS.
type SearchResult = {
    refIndex: number;
    item: FlatTreeField;
};

// Check if any of the possible value of a Node in the Domain tree
// is a TreeField[], a.k.a a leaf of the tree.
const _isFields = (
    node:
        | CategoryWithTableOnly
        | TableWithFields
        | InternalVertex
        | TreeField[],
): node is TreeField[] => {
    return Array.isArray(node);
};

// The type used for the DFS traversal of the tree.
type _NodeTraversal = [
    string,
    CategoryWithTableOnly | TableWithFields | TreeField[],
];

/**
 * Flatten the tree data structure used by the UI to make it into
 * a list that fuse-js can search through.
 * Exemple:
 * `{a: {b: {c: [{field: 123}]}}}` would turn into:
 * `[{path: '.a.b.c', value: {field: 123}}]`.
 */
const flattenTree = (tree: DomainWithFields): FlatTreeField[] => {
    const flatTree: FlatTreeField[] = [];
    // Use an adpated DFS algorithm to traverse the whole tree.
    for (const rootKey of Object.keys(tree)) {
        // To be able to return the path in the tree for all field,
        // we need to keep track of it during the traversal.
        // So in our stack, we keep the node to traverse and its path in the tree.
        const stack: _NodeTraversal[] = [[rootKey, tree[rootKey]]];
        while (stack.length > 0) {
            const node = stack.pop();

            // Should never happen, but needed for TS to type
            // correctly.
            if (node === undefined) {
                continue;
            }

            if (_isFields(node[1])) {
                // Reached a leaf. Add all the field to our flat list
                node[1].forEach((field) =>
                    flatTree.push({
                        path: node[0],
                        table: translateBlock(field.table),
                        value: field,
                    }),
                );
            } else {
                // Reach a table or category. Add all its children to the stack
                // to be traversed.
                const tableOrCategory = node[1];
                Object.keys(tableOrCategory).forEach((childKey) => {
                    stack.push([
                        `${node[0]}.${childKey}`,
                        tableOrCategory[childKey],
                    ]);
                });
            }
        }
    }

    return flatTree;
};

// Type for the tree. A leaf if an array of tree field,
// and any other node is an object, where the keys are
// the name of the table/domain.
type Node = InternalVertex | TreeField[];
interface InternalVertex {
    [key: string]: Node;
}

// The opposite of the isLeaf typeguard, to check if we are an object (which in our tree means we are not a leaf).
const _isNotLeaf = (node: Node): node is InternalVertex => {
    return !_isFields(node);
};

/**
 From the flat search result returned by fuse-js, regenerate the tree data structure
 expected by the UI library.
 As an exemple, as input, we get a flat list of search result returned by fusejs, with
 each item having its path in the original tree:
 ```
 [
    { ..., item: {path: 'a.b.c', value: val1} },
    { ..., item: {path: 'a.b.c', value: val2} },
    { ..., item: {path: 'a.b.d', value: val3} },
 ]
 ```
 and we should output the tree-version compatible with the UI:
 ```
 {'a': {'b': {'c': [val1, val2]}, 'd': [val3] } }
 ```
 */
const treeFromSearch = (search: SearchResult[]): DomainWithFields => {
    // We start with an empty tree and we are going to had the node
    // by iterating through our search result list.
    const tree = {};

    for (const fieldRes of search) {
        // Each item contain its path in the original tree, in the dot-format (similar to
        // what lodash use. ex: 'key1.key2.key3'). We split it to get all the individual key making
        // up the final path of the item in the tree.
        const keys: string[] = fieldRes.item.path.split('.');

        // We usually have multiple key, because the tree structure is an object of object,
        // i.e: `{ software1: { domain1: { table1: [{field1}, {field2}], table2: [{field3}] } } }`
        // We have to build the object-tree from the bottom up, so we need to keep a reference to
        // the parent.
        // The first parent will always be the root of the tree, then, as we build the tree, it will
        // be the last node inserted.
        let objRef: Node = tree;
        keys.forEach((key, i) => {
            // When we reach the last key, it means we reach the object that will contains our "leaves",
            // a.k.a, the object where each of its key contain a array of TreeField.
            // It is not a leaf in itself (its children are), that is why we need to assert _isNotLeaf so
            // that typescript type-check correctly.
            if (i === keys.length - 1 && _isNotLeaf(objRef)) {
                if (key in objRef) {
                    // If we already created the list of field for this leaf,
                    // we just add to it.
                    const fieldList = objRef[key] as TreeField[];
                    fieldList.push(fieldRes.item.value);
                } else {
                    // If it is the first time we reach this leaf, create the field list.
                    objRef[key] = [fieldRes.item.value];
                }
            } else {
                // If we didn't reach a leaf, continue to construct the tree.
                // Using the _isNotLeaf is not necessary, it is just here
                // to statisfy TS type check.
                if (key in objRef && _isNotLeaf(objRef)) {
                    objRef = objRef[key];
                } else if (_isNotLeaf(objRef)) {
                    objRef[key] = {};
                    objRef = objRef[key];
                }
            }
        });
    }

    return tree;
};

/**
 * Return the nodeIds to expend from the search result.
 */
const expendNodeIdFromSearch = (search: SearchResult[]): string[] => {
    const nodeIds: Set<string> = new Set();
    for (const item of search) {
        // It is possible for a field to be at the 2nd depth level.
        // In this case, it will only have one entry in its path,
        // so we just add this.
        if (!item.item.path.includes('.')) {
            nodeIds.add(item.item.path);
            continue;
        }

        // Get the root of the path, i.e the first key of the tree.
        // A.K.A the "domaine" (finance, hr, ...).
        const root = item.item.path.substring(
            0,
            item.item.path.indexOf('.', 1),
        );
        // The domain has the same nodeId than its key in the tree data structure.
        nodeIds.add(root);
        // The "leaves" are a concatenation of the root (domain) + the name of their table.
        nodeIds.add(`${root}-table-${item.item.value.table}`);
    }
    return Array.from(nodeIds);
};

const DEFAULT_PARAMETERS = {
    threshold: 0.2,
    keys: ['value.label', 'table'],
};
/**
 * This helper returns a treeData with only the nodes that match the search query.
 * At each level we assume nodes could be either objects (categories, tables) or arrays of fields, so we handle both cases.
 * TODO: refactor this old function along with the whole treeData logic, now that we have a more "fluid" tree structure.
 * @param treeData the initial treeData
 * @param setFilteredTreeData the callback to set the filtered treeData
 * @param setExpanded the list of nodes that should be expanded in the TreeView
 */
export const filterAndExpandFieldSearchResults =
    (
        treeData: DomainWithFields,
        setFilteredTreeData: React.Dispatch<
            React.SetStateAction<DomainWithFields>
        >,
        setExpanded: (arg1: Expanded) => void,
        overrideFuseParameters?: Partial<IFuseOptions<FlatTreeField>>,
    ) =>
    (usedQuery: string | undefined) => {
        const fuseParameters = {
            ...DEFAULT_PARAMETERS,
            ...overrideFuseParameters,
        };
        if (!usedQuery) {
            return;
        }

        // fuse-js expects a flat list of object to search through, it cannot handle our
        // tree data structure.
        // So first, we turn our tree into a flat list.
        // To be able to rebuild the tree later, we keep the path of each object in the tree.
        const flatTree = flattenTree(treeData);

        // Initialize the fuzzy engine
        const fuseOptions = {
            isCaseSensitive: false, // Ignore case
            ignoreDiacritics: true, // Ignore accent
            threshold: fuseParameters.threshold, // Define how close we want the match. 0 is a perfect match.
            ignoreLocation: true, // We don't care where the words are within the sentence.
            keys: fuseParameters.keys, // Path inside the object to search.
        };
        const fuse = new Fuse(flatTree, fuseOptions);

        // Using this form of query we try to fuzzy match all the words
        // independently of where they are in the sentence.
        // Passing just the `usedQuery` would result in a sentence fuzzy match.
        // This would mean that, for exemple:
        // "montant exercice" would not match "montant de l'exercice" because
        // we have "de l'" between "montant" and "exercice".
        // But with this approach, it match both work independently, so it would
        // find both "montant" and "exercice" in the sentence.
        // It also means that the order does not matter:
        // "montant exercice" would match "exercice du montant recette".
        // const fuseQuery = usedQuery;
        const fuseQuery = {
            $or: fuseParameters.keys!.map((key) => {
                const path =
                    typeof key === 'string' || Array.isArray(key)
                        ? key
                        : key.name;
                return {
                    $and: [
                        ...usedQuery
                            .split(' ')
                            .filter((v) => v !== '')
                            .map((v) => ({
                                $path: path.toString(),
                                $val: v,
                            })),
                    ],
                };
            }),
        };
        const search = fuse.search(fuseQuery);

        const newTreeData = treeFromSearch(search);
        const newExpanded = expendNodeIdFromSearch(search);
        setFilteredTreeData(newTreeData);
        setExpanded(newExpanded);
    };

export const getTreeDataDomains = (
    metric: SentenceMetricT,
    treeData: TreeData,
) => {
    if (!metric.functions) {
        return Object.keys(treeData);
    }
    switch (metric.functions.length) {
        case 0:
            return Object.keys(treeData);
        case 1:
            return metric.functions;
        default:
            return [...metric.functions, GLOBAL];
    }
};
